import React, { useState, useEffect, useRef } from 'react';
import { Search, Check } from 'lucide-react';
import $ from 'jquery';
import 'datatables.net-dt';
import axios from 'axios';

// DataTables 및 반응형 확장에 필요한 스타일시트 임포트
import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';

// DataTables 반응형 확장 기능 활성화
import 'datatables.net-responsive';

const FarmSearchPage = ({ selectedFarm, setSelectedFarm, searchResults, setSearchResults, setCurrentPage }) => {
    const tableRef = useRef(null);
    const dtApi = useRef(null);
    const selectedFarmRef = useRef(selectedFarm); // selectedFarm의 최신 값을 추적하기 위한 ref

    const [searchText, setSearchText] = useState('');

    const handleSearch = async () => {
        const query = searchText.trim();
        if (!query) {
            // 검색어가 없으면 결과를 비웁니다.
            setSearchResults([]);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('farm', query);
            const response = await axios.post('/api/farm', formData);
            const farms = response.data?.list || [];
            setSearchResults(farms);
        } catch (error) {
            console.error("농장 검색 실패:", error);
            alert('농장 검색 중 오류가 발생했습니다.');
            setSearchResults([]);
        }
    };

    const handleSelectFarm = (farm) => {
        // API 응답 구조에 맞춰 selectedFarm 상태를 설정합니다.
        // id, name, owner 필드를 일관되게 사용합니다.
        setSelectedFarm({
            id: farm.member_id,
            name: farm.farm,
            owner: farm.name,
        });
        // 씨수소 선택 페이지로 바로 이동하지 않고, 사용자가 메뉴를 통해 이동하도록 합니다.
        // setCurrentPage('sireSelection', true); 
    };

    // selectedFarm prop이 변경될 때마다 ref를 업데이트합니다.
    useEffect(() => {
        selectedFarmRef.current = selectedFarm;
    }, [selectedFarm]);

    const columns = [
        { title: '농장주', data: 'name' },
        { title: '농장명', data: 'farm' },
        { title: '농장 ID', data: 'member_id' },
        { title: '회원등급', data: 'membership' },
        { title: '전화번호', data: 'f_tel' },
        { title: '휴대폰', data: 'mobile' },
        { title: '주소', data: 'f_juso' },
        { title: '선택', data: 'member_id' }
    ];

    useEffect(() => {
        const options = {
            columns: columns,
            responsive: {
                details: { type: 'inline' }
            },
            paging: true,
            lengthChange: false,
            pageLength: 10,
            searching: false,
            ordering: true,
            language: {
                emptyTable: "검색 결과가 없습니다.",
                paginate: { first: "처음", last: "마지막", next: "다음", previous: "이전" },
                info: "_TOTAL_건 중 _START_에서 _END_까지 표시",
                infoEmpty: "0건",
                infoFiltered: "(_MAX_건에서 필터링됨)"
            },
            columnDefs: [
                { responsivePriority: 2, targets: 1 }, // 농장명
                { responsivePriority: 3, targets: 2 }, // 농장 ID
                {
                    targets: 7, // 선택 버튼
                    responsivePriority: 1,
                    orderable: false,
                    className: 'dt-body-right',
                    render: (data, type, row) => {
                        // 항상 버튼을 렌더링하고, 상태 변경은 drawCallback에서 처리합니다.
                        return `<button class="datatable-action-button secondary-button" data-farm-id="${row.member_id}">선택</button>`;
                    }
                }
            ],
            drawCallback: function (settings) {
                const api = this.api();
                api.responsive.recalc();

                // 선택된 농장 상태 업데이트
                api.rows({ page: 'current' }).nodes().to$().each(function () {
                    const row = $(this);
                    const rowIndex = api.row(this).index();
                    const rowData = api.row(this).data();
                    const actionCell = row.find('td:last-child');

                    // 1. 먼저 모든 행을 기본 상태(선택 버튼)로 되돌립니다.
                    row.css('background-color', '');
                    // '선택됨' span이 있다면 원래 버튼으로 되돌립니다.
                    if (actionCell.find('.datatable-action-button').length === 0) {
                        const buttonHtml = api.cell(rowIndex, 7).render('display'); // 7번째 컬럼의 render 함수를 다시 실행
                        actionCell.html(buttonHtml);
                    }

                    // 2. 현재 선택된 농장인 경우에만 '선택됨' 상태로 변경합니다.
                    if (rowData && selectedFarmRef.current && rowData.member_id === selectedFarmRef.current.id) {
                        row.css('background-color', '#eff6ff');
                        actionCell.html(`<span class="datatable-completed-span" data-farm-id="${rowData.member_id}" style="justify-content: flex-end;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> 선택됨</span>`);
                    }
                });
            }
        };

        dtApi.current = $(tableRef.current).DataTable(options);

        $(tableRef.current).on('click', '.datatable-action-button', function () {
            // data-* 속성에서 가져온 값은 문자열이므로, 그대로 사용합니다.
            const farmId = $(this).data('farm-id').toString();
            // React state(searchResults)에 의존하는 대신, DataTables API로 클릭된 행의 데이터를 직접 가져옵니다.
            const row = dtApi.current.row($(this).closest('tr'));
            const farmToSelect = row.data();
            // farmToSelect.member_id와 farmId를 비교합니다.
            if (farmToSelect && farmToSelect.member_id === farmId) {
                handleSelectFarm(farmToSelect);
            }
        });

        return () => {
            if (dtApi.current) {
                $(tableRef.current).off('click');
                dtApi.current.destroy();
                dtApi.current = null;
            }
        };
    }, []); // 마운트 시 한 번만 실행

    // 검색 결과가 변경될 때 테이블 데이터 업데이트
    useEffect(() => {
        if (dtApi.current) {
            dtApi.current.clear().rows.add(searchResults).draw();
        }
    }, [searchResults]);

    // 선택된 농장이 변경될 때 테이블 다시 그리기 (UI 업데이트)
    useEffect(() => {
        selectedFarmRef.current = selectedFarm;
        if (dtApi.current) {
            // dtApi.current.draw(false); // Removed to prevent scroll jump

            // Manually update the visual state
            const api = dtApi.current;

            // Find all elements with data-farm-id (buttons and spans) in the table
            $(tableRef.current).find('[data-farm-id]').each(function () {
                const element = $(this);
                const farmId = element.data('farm-id').toString();
                const isSelected = selectedFarm && selectedFarm.id && farmId === selectedFarm.id.toString();
                const row = element.closest('tr');

                // If it's a child row (responsive), we might need to find the parent row to style it?
                // But styling the row background usually applies to the main row.
                // The element might be in the main row or child row.

                if (isSelected) {
                    // Change to span if it's a button
                    if (element.hasClass('datatable-action-button')) {
                        const spanHtml = `<span class="datatable-completed-span" data-farm-id="${farmId}" style="justify-content: flex-end;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> 선택됨</span>`;
                        element.parent().html(spanHtml); // Replace content of the cell/container
                    }
                    // Style the row (only if it's a main row, usually has role='row' or similar, but checking closest tr is fine)
                    // Note: child rows usually have class 'child'. We probably don't want to style them blue?
                    // But the original code styled the row: `row.css('background-color', '#eff6ff');`
                    if (!row.hasClass('child')) {
                        row.css('background-color', '#eff6ff');
                    }
                } else {
                    // Change to button if it's a span
                    if (element.hasClass('datatable-completed-span')) {
                        const buttonHtml = `<button class="datatable-action-button secondary-button" data-farm-id="${farmId}">선택</button>`;
                        element.parent().html(buttonHtml);
                    }
                    if (!row.hasClass('child')) {
                        row.css('background-color', '');
                    }
                }
            });
        }
    }, [selectedFarm]);

    return (
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 className="page-title">농장 검색 및 선택</h2>

            <div className="card">
                <div className="search-bar-container">
                    <input
                        type="search"
                        placeholder="농장명 또는 전화번호를 입력하세요"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="form-input"
                    />
                    <button
                        onClick={handleSearch}
                        className="action-button"
                        style={{ padding: '0.75rem 1rem', flexShrink: 0 }}
                    >
                        <Search size={20} style={{ marginRight: '0.5rem' }} />
                        검색
                    </button>
                </div>
            </div>

            {selectedFarm && selectedFarm.id && (
                <div className="card" style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                    <p style={{ fontWeight: '600', color: '#047857' }}>✅ 현재 선택된 농장: <span style={{ color: '#059669' }}>{selectedFarm.name} ({selectedFarm.id})</span></p>
                </div>
            )}

            <div className="card">
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>검색 결과 ({searchResults.length}건)</h3>
                <div className="table-container">
                    <table ref={tableRef} className="display nowrap" style={{ width: '100%' }}>
                        {/* 헤더와 내용은 DataTables가 채웁니다. */}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FarmSearchPage;
